import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import VoiceSectionPage from '@/components/layout/VoiceSectionPage'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/supabaseClient'

const strategyLabel = {
  sequencial: 'Sequencial',
  'menos-ocupado': 'Menos ocupado',
  aleatorio: 'Aleatório',
}

function encodeDescription(description, strategy) {
  const clean = (description || '').trim()
  return clean ? `[voice_strategy=${strategy}] ${clean}` : `[voice_strategy=${strategy}]`
}

function decodeQueue(row) {
  const raw = row.description || ''
  const match = raw.match(/^\[voice_strategy=([^\]]+)\]\s*/)
  const strategy = match?.[1] || 'sequencial'
  const description = raw.replace(/^\[voice_strategy=[^\]]+\]\s*/, '')

  return {
    id: row.id,
    name: row.name,
    isActive: row.is_active !== false,
    strategy,
    description,
    priority: row.priority ?? 2,
  }
}

export default function FilasPage() {
  const { getProfileId } = useAuth()
  const profileId = getProfileId()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState('')

  const [queues, setQueues] = useState([])
  const [name, setName] = useState('')
  const [strategy, setStrategy] = useState('sequencial')

  const activeCount = useMemo(() => queues.filter((q) => q.isActive).length, [queues])

  const loadQueues = useCallback(async () => {
    if (!profileId) {
      setQueues([])
      setLoading(false)
      return
    }

    setLoading(true)
    const { data, error } = await supabase
      .from('queues')
      .select('id, name, description, is_active, priority')
      .eq('profile_id', profileId)
      .order('priority', { ascending: true })
      .order('name', { ascending: true })

    if (error) {
      console.error('[FilasPage]', error)
      toast.error('Não foi possível carregar as filas da sua conta.')
      setQueues([])
    } else {
      setQueues((data || []).map(decodeQueue))
    }
    setLoading(false)
  }, [profileId])

  useEffect(() => {
    loadQueues()
  }, [loadQueues])

  async function addQueue(e) {
    e.preventDefault()
    const queueName = name.trim()
    if (!queueName) {
      toast.error('Informe o nome da fila.')
      return
    }
    if (!profileId) {
      toast.error('Perfil não identificado.')
      return
    }

    setSaving(true)
    const nextPriority = queues.length > 0 ? Math.max(...queues.map((q) => q.priority || 1)) + 1 : 1

    const payload = {
      profile_id: profileId,
      name: queueName,
      description: encodeDescription('', strategy),
      is_active: true,
      priority: nextPriority,
      default_channel: 'WhatsApp',
      color_tag: 'wappdesk-primary',
      allow_ai: true,
    }

    const { error } = await supabase.from('queues').insert([payload])
    setSaving(false)

    if (error) {
      if (error.code === '23505') {
        toast.error('Já existe uma fila com esse nome.')
      } else {
        toast.error(error.message || 'Erro ao criar fila.')
      }
      return
    }

    setName('')
    setStrategy('sequencial')
    toast.success('Fila criada.')
    await loadQueues()
  }

  async function toggleQueue(queue) {
    setSaving(true)
    const { error } = await supabase
      .from('queues')
      .update({ is_active: !queue.isActive })
      .eq('id', queue.id)
      .eq('profile_id', profileId)

    setSaving(false)
    if (error) {
      toast.error(error.message || 'Erro ao atualizar fila.')
      return
    }

    setQueues((prev) => prev.map((q) => (q.id === queue.id ? { ...q, isActive: !q.isActive } : q)))
  }

  async function removeQueue(queueId) {
    setDeletingId(queueId)
    const { error } = await supabase
      .from('queues')
      .delete()
      .eq('id', queueId)
      .eq('profile_id', profileId)
    setDeletingId('')

    if (error) {
      toast.error(error.message || 'Erro ao remover fila.')
      return
    }

    setQueues((prev) => prev.filter((q) => q.id !== queueId))
    toast.success('Fila removida.')
  }

  return (
    <VoiceSectionPage
      title="Configurar filas"
      description="Aqui você gerencia as mesmas filas usadas no WappDesk."
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
          {queues.length} fila(s), {activeCount} ativa(s)
        </div>

        <form
          onSubmit={addQueue}
          className="rounded-xl border border-border bg-card p-4 grid gap-3 sm:grid-cols-3"
        >
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome da fila (ex: Comercial)"
            className="h-11 rounded-lg border border-input bg-background px-3 text-sm sm:col-span-2"
          />
          <select
            value={strategy}
            onChange={(e) => setStrategy(e.target.value)}
            className="h-11 rounded-lg border border-input bg-background px-3 text-sm"
          >
            <option value="sequencial">Sequencial</option>
            <option value="menos-ocupado">Menos ocupado</option>
            <option value="aleatorio">Aleatório</option>
          </select>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground sm:col-span-3 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Adicionar fila
          </button>
        </form>

        <ul className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border">
          {loading ? (
            <li className="px-4 py-5 text-sm text-muted-foreground">Carregando filas...</li>
          ) : queues.length === 0 ? (
            <li className="px-4 py-5 text-sm text-muted-foreground">Nenhuma fila cadastrada.</li>
          ) : (
            queues.map((queue) => (
              <li key={queue.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div>
                  <p className="font-medium text-foreground">{queue.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Estratégia: {strategyLabel[queue.strategy] || 'Sequencial'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleQueue(queue)}
                    disabled={saving}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                      queue.isActive
                        ? 'bg-emerald-500/15 text-emerald-700'
                        : 'bg-slate-500/15 text-slate-700'
                    }`}
                  >
                    {queue.isActive ? 'Ativa' : 'Inativa'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeQueue(queue.id)}
                    disabled={deletingId === queue.id}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10 disabled:opacity-50"
                  >
                    {deletingId === queue.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </VoiceSectionPage>
  )
}
